import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/stockStatus";
import { formatDurationLabel } from "./ServiceDurationForm";

function SectionLabel({ children }) {
  return (
    <MUI.Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontWeight: 700, letterSpacing: "0.06em" }}
    >
      {children}
    </MUI.Typography>
  );
}

function DetailField({ label, value, emphasize }) {
  return (
    <MUI.Box>
      <MUI.Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
      >
        {label}
      </MUI.Typography>
      <MUI.Typography
        variant={emphasize ? "subtitle1" : "body2"}
        sx={{ fontWeight: emphasize ? 700 : 500, mt: 0.25 }}
        color={emphasize ? "primary.main" : "text.primary"}
      >
        {value}
      </MUI.Typography>
    </MUI.Box>
  );
}

function SectionCard({ label, children }) {
  return (
    <MUI.Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, borderColor: "divider" }}>
      <SectionLabel>{label}</SectionLabel>
      <MUI.Box sx={{ mt: 1.75 }}>{children}</MUI.Box>
    </MUI.Paper>
  );
}

// Mirrors WarehouseItemDetailsContent.jsx's SectionCard/DetailField grid
// exactly, including the PricingAndStock-style layout for the new price
// fields "in the same manner they exist on an item".
export default function WarehouseServiceDetailsContent({ service, currency }) {
  const { t } = useTranslation();
  const hasPricing = service?.price != null || service?.pdv != null;

  return (
    <MUI.Stack spacing={2.5}>
      <SectionCard label={t("BasicInformation")}>
        <MUI.Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            rowGap: 2.5,
            columnGap: 2,
          }}
        >
          <DetailField label={t("Title")} value={service?.title} />
          <DetailField
            label={t("Duration")}
            value={service?.duration ? formatDurationLabel(service.duration) : "N/A"}
          />
          <MUI.Box sx={{ gridColumn: "1 / -1" }}>
            <DetailField
              label={t("Description")}
              value={service?.description || "N/A"}
            />
          </MUI.Box>
        </MUI.Box>
      </SectionCard>

      {hasPricing && (
        <SectionCard label={t("PricingAndStock")}>
          <MUI.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
              rowGap: 2.5,
              columnGap: 2,
            }}
          >
            <DetailField
              label={t("Price")}
              value={service?.price != null ? formatCurrency(service.price, currency) : "N/A"}
            />
            <DetailField
              label={t("PDV")}
              value={service?.pdv != null ? `${Number(service.pdv)}%` : "N/A"}
            />
            <DetailField
              label={t("PriceWithVat")}
              value={service?.priceWithTax != null ? formatCurrency(service.priceWithTax, currency) : "N/A"}
              emphasize
            />
          </MUI.Box>
        </SectionCard>
      )}
    </MUI.Stack>
  );
}
